import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, RadialBarChart, RadialBar, Legend } from 'recharts';

interface VoteCountChartProps {
  totalVotes: number;
  upVotes: number;
  downVotes: number;
  conversionRate: number;
  isLoading?: boolean;
}

const COLORS = {
  positive: '#10B981',
  negative: '#EF4444',
  neutral: '#6B7280'
};

export function VoteCountChart({ totalVotes, upVotes, downVotes, conversionRate, isLoading }: VoteCountChartProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="h-4 bg-gray-200 rounded animate-pulse mb-6"></div>
        <div className="h-80 bg-gray-100 rounded animate-pulse"></div>
      </div>
    );
  }

  const voteData = [
    { name: 'Positive', value: upVotes, color: COLORS.positive },
    { name: 'Negative', value: downVotes, color: COLORS.negative }
  ];

  const conversionData = [
    {
      name: 'Conversion Rate',
      value: conversionRate * 100,
      fill: conversionRate > 0.7 ? '#10B981' : conversionRate > 0.5 ? '#F59E0B' : '#EF4444'
    }
  ];

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3">
          <p className="font-semibold text-gray-900">{data.name}</p>
          <p style={{ color: data.color }}>
            <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: data.color }}></span>
            Votes: {data.value.toLocaleString()}
            {totalVotes > 0 && (
              <span className="text-gray-500 ml-2">
                ({((data.value / totalVotes) * 100).toFixed(1)}%)
              </span>
            )}
          </p>
        </div>
      );
    }
    return null;
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={14}
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(1)}%`}
      </text>
    );
  };

  const getConversionDescription = (rate: number) => {
    if (rate >= 0.8) return { text: 'Excellent', color: 'text-green-600' };
    if (rate >= 0.6) return { text: 'Good', color: 'text-green-500' };
    if (rate >= 0.4) return { text: 'Average', color: 'text-yellow-500' };
    if (rate >= 0.2) return { text: 'Poor', color: 'text-orange-500' };
    return { text: 'Very Poor', color: 'text-red-500' };
  };

  const conversionDesc = getConversionDescription(conversionRate);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Vote Analytics</h3>
          <p className="text-sm text-gray-600">Feedback sentiment and conversion metrics</p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-indigo-600">📊</div>
        </div>
      </div>

      {totalVotes > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Vote Distribution Pie Chart */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Vote Distribution</h4>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={voteData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                  animationBegin={0}
                  animationDuration={800}
                >
                  {voteData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Conversion Rate Gauge */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Conversion Rate</h4>
            <div className="flex flex-col items-center">
              <ResponsiveContainer width="100%" height={200}>
                <RadialBarChart 
                  cx="50%" 
                  cy="50%" 
                  innerRadius="60%" 
                  outerRadius="90%" 
                  data={conversionData}
                  startAngle={180}
                  endAngle={0}
                >
                  <RadialBar
                    dataKey="value"
                    cornerRadius={10}
                    fill={conversionData[0].fill}
                  />
                </RadialBarChart>
              </ResponsiveContainer>
              <div className="text-center mt-4">
                <div className="text-3xl font-bold" style={{ color: conversionData[0].fill }}>
                  {(conversionRate * 100).toFixed(1)}%
                </div>
                <div className={`text-sm font-medium ${conversionDesc.color}`}>
                  {conversionDesc.text}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">📊</div>
          <p className="text-gray-500">No vote data available yet</p>
          <p className="text-sm text-gray-400">Data will appear as feedback is received</p>
        </div>
      )}

      {/* Summary Statistics */}
      {totalVotes > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{totalVotes.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Total Votes</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{upVotes.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Positive</div>
              <div className="text-xs text-gray-400">
                {totalVotes > 0 ? ((upVotes / totalVotes) * 100).toFixed(1) : 0}%
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{downVotes.toLocaleString()}</div>
              <div className="text-sm text-gray-500">Negative</div>
              <div className="text-xs text-gray-400">
                {totalVotes > 0 ? ((downVotes / totalVotes) * 100).toFixed(1) : 0}%
              </div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {upVotes > downVotes ? '👍' : downVotes > upVotes ? '👎' : '➖'}
              </div>
              <div className="text-sm text-gray-500">Sentiment</div>
              <div className="text-xs text-gray-400">
                {upVotes > downVotes ? 'Positive' : downVotes > upVotes ? 'Negative' : 'Neutral'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 